// © 2025 GridStorm / Tekivex — All Rights Reserved
// Unauthorized reproduction or distribution is prohibited.
/**
 * Sentinel — the framework-agnostic monitoring controller.
 *
 * Feed it anomaly events (from any DataFlow engine) plus the live row buffer.
 * It applies severity gating + per-column dedupe + a global rate limit (cost
 * guards), runs the deterministic investigator, emits the Finding via
 * `onFinding`, then best-effort enriches it (pluggable AI) and routes it to the
 * configured notifiers.
 *
 * No React, no DOM — usable in the browser, Node, a worker, or any framework.
 * The React hook (`@gridstorm/dataflow-sentinel/react`) is a thin wrapper.
 */

import type { AnomalyEvent, StreamRow } from '@gridstorm/dataflow-core';
import type { Finding, FindingSeverity, SentinelConfig } from './types.js';
import { investigate, enrichmentContext } from './investigator.js';
import type { EnrichProvider } from './enrich.js';
import type { Notifier } from './notifier.js';

const SEV_RANK: Record<FindingSeverity, number> = { info: 0, warning: 1, critical: 2 };

export interface SentinelOptions {
  config:          SentinelConfig;
  /** Called with each new finding (deterministic), and again if it gets enriched. */
  onFinding:       (finding: Finding) => void;
  /** Optional pluggable AI enrichment. */
  enrichProvider?: EnrichProvider;
  /** Optional outbound notifiers (webhook, callback, custom). */
  notifiers?:      Notifier[];
  /** Called when an anomaly is suppressed by a gate (for UI counters). */
  onSuppressed?:   () => void;
}

export class Sentinel {
  private _config:    SentinelConfig;
  private _onFinding: (f: Finding) => void;
  private _enrich?:   EnrichProvider;
  private _notifiers: Notifier[];
  private _onSuppressed?: () => void;

  private _seen           = new Set<string>();
  private _lastAt         = 0;
  private _lastByColumn   = new Map<string, number>();

  constructor(opts: SentinelOptions) {
    this._config       = opts.config;
    this._onFinding    = opts.onFinding;
    this._enrich       = opts.enrichProvider;
    this._notifiers    = opts.notifiers ?? [];
    this._onSuppressed = opts.onSuppressed;
  }

  /** Hot-swap config (e.g. user toggles severity / enable). */
  setConfig(config: SentinelConfig): void { this._config = config; }
  setEnrichProvider(p: EnrichProvider | undefined): void { this._enrich = p; }
  setNotifiers(n: Notifier[]): void { this._notifiers = n; }

  /** Clear dedupe/rate-limit memory. */
  reset(): void {
    this._seen.clear();
    this._lastByColumn.clear();
    this._lastAt = 0;
  }

  private now(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  /**
   * Ingest one anomaly event with the current row buffer.
   * Returns the Finding if one was created, else null (gated/suppressed).
   */
  ingest(anomaly: AnomalyEvent, rows: readonly StreamRow[]): Finding | null {
    const cfg = this._config;
    if (!cfg.enabled) return null;
    if (this._seen.has(anomaly.id)) return null;
    this._seen.add(anomaly.id);

    const sev = (anomaly.severity as FindingSeverity) ?? 'info';
    if (SEV_RANK[sev] < SEV_RANK[cfg.minSeverity]) { this._onSuppressed?.(); return null; }

    const now = this.now();
    const lastCol = this._lastByColumn.get(anomaly.columnId) ?? -Infinity;
    if (now - lastCol < cfg.dedupeWindowMs)  { this._onSuppressed?.(); return null; }
    if (now - this._lastAt < cfg.rateLimitMs) { this._onSuppressed?.(); return null; }

    this._lastAt = now;
    this._lastByColumn.set(anomaly.columnId, now);

    const finding = investigate({ anomaly, rows });

    // Enforce minSeverity on the FINDING's severity too. The pre-gate above
    // uses the engine's anomaly severity (its own thresholds, e.g. warning at
    // 2.5σ), while the investigator grades stricter (warning at 3σ) — without
    // this check a 2.6σ event slips through as an "info" finding despite
    // minSeverity: 'warning'.
    if (SEV_RANK[finding.severity] < SEV_RANK[cfg.minSeverity]) {
      this._onSuppressed?.();
      return null;
    }

    this._onFinding(finding);
    void this._tail(anomaly, rows, finding);
    return finding;
  }

  /** Convenience: ingest a batch, skipping already-seen ids. */
  ingestMany(anomalies: readonly AnomalyEvent[], rows: readonly StreamRow[]): void {
    for (const a of anomalies) this.ingest(a, rows);
  }

  private async _tail(anomaly: AnomalyEvent, rows: readonly StreamRow[], finding: Finding): Promise<void> {
    let final = finding;

    if (this._config.useAi && this._enrich) {
      const ctrl = new AbortController();
      try {
        const ctx = enrichmentContext({ anomaly, rows }, finding);
        const enr = await this._enrich.enrich({ finding, contextJson: ctx }, ctrl.signal);
        if (enr.cause || enr.suggestedAction) {
          final = {
            ...finding,
            cause:           enr.cause ?? finding.cause,
            suggestedAction: enr.suggestedAction ?? finding.suggestedAction,
            enriched:        true,
          };
          this._onFinding(final);
        }
      } catch { /* enrichment is best-effort */ }
    }

    for (const n of this._notifiers) {
      void n.notify(final).catch(() => undefined);
    }
  }
}
